import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    
    if (href.startsWith('#')) {
      if (isHomePage) {
        // On home page, scroll to section
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // On other pages, navigate to home then scroll
        window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/');
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
  };

  const navItems = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '/about' },
    { label: 'Services', href: '#services' },
    { label: 'Process', href: '#process' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-2'
          : 'bg-transparent py-3 lg:py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/');
            }}
            className="cursor-pointer"
          >
            <img
              src="https://asili.co.ug/visuals/assets/img/logo-AsiliVisuals.png"
              alt="Asili Visuals Logo"
              className="h-8 sm:h-10 w-auto"
            />
          </a>
        </div>

        {/* Desktop menu */}
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => {
                e.preventDefault();
                if (item.href.startsWith('/')) {
                  window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE(item.href);
                } else {
                  handleNavClick(item.href);
                }
              }}
              className={`relative px-3 xl:px-4 py-2 text-sm font-medium transition-all whitespace-nowrap cursor-pointer group ${
                scrolled
                  ? 'text-gray-700 hover:text-[#038C7F]'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              {item.label}
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#038C7F] transition-all duration-300 group-hover:w-6"></span>
            </a>
          ))}
          <a
            href="/portfolio"
            onClick={(e) => {
              e.preventDefault();
              window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/portfolio');
            }}
            className={`relative px-3 xl:px-4 py-2 text-sm font-medium transition-all whitespace-nowrap cursor-pointer group ${
              scrolled
                ? 'text-gray-700 hover:text-[#038C7F]'
                : 'text-white/80 hover:text-white'
            }`}
          >
            Portfolio
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#038C7F] transition-all duration-300 group-hover:w-6"></span>
          </a>
          <a
            href="/contact"
            onClick={(e) => {
              e.preventDefault();
              window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/contact');
            }}
            className={`relative px-3 xl:px-4 py-2 text-sm font-medium transition-all whitespace-nowrap cursor-pointer group ${
              scrolled
                ? 'text-gray-700 hover:text-[#038C7F]'
                : 'text-white/80 hover:text-white'
            }`}
          >
            Contact
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#038C7F] transition-all duration-300 group-hover:w-6"></span>
          </a>
        </div>

        {/* CTA + Mobile toggle */}
        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href="/contact"
            onClick={(e) => {
              e.preventDefault();
              window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/contact');
            }}
            className={`hidden sm:flex group px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer items-center gap-2 ${
              scrolled
                ? 'bg-[#038C7F] text-white hover:bg-[#0D0D0D]'
                : 'bg-white text-[#0D0D0D] hover:bg-[#F2780C] hover:text-white'
            }`}
          >
            <span>Work With Us</span>
            <i className="ri-arrow-right-up-line text-base sm:text-lg group-hover:rotate-45 transition-transform duration-300"></i>
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled ? 'text-[#0D0D0D] hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl transition-all duration-300 ${
          mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="px-4 py-4 space-y-1">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => {
                e.preventDefault();
                setMobileMenuOpen(false);
                if (item.href.startsWith('/')) {
                  window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE(item.href);
                } else {
                  handleNavClick(item.href);
                }
              }}
              className="block px-4 py-3 text-sm font-medium text-gray-700 hover:text-[#038C7F] hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
            >
              {item.label}
            </a>
          ))}
          <a
            href="/portfolio"
            onClick={(e) => {
              e.preventDefault();
              setMobileMenuOpen(false);
              window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/portfolio');
            }}
            className="block px-4 py-3 text-sm font-medium text-gray-700 hover:text-[#038C7F] hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
          >
            Portfolio
          </a>
          <a
            href="/contact"
            onClick={(e) => {
              e.preventDefault();
              setMobileMenuOpen(false);
              window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/contact');
            }}
            className="block px-4 py-3 text-sm font-medium text-gray-700 hover:text-[#038C7F] hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
          >
            Contact
          </a>
          <a
            href="/contact"
            onClick={(e) => {
              e.preventDefault();
              setMobileMenuOpen(false);
              window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/contact');
            }}
            className="block mt-3 px-4 py-3 bg-[#038C7F] text-white text-sm font-semibold rounded-lg text-center cursor-pointer"
          >
            Work With Us
          </a>
        </div>
      </div>
    </nav>
  );
}
